import "./KnowYourCustomerForm.scss";

import { Box } from "@material-ui/core";
import { ApiErrors } from "common/backend/api-errors/errors";
import { Flow } from "common/components";
import { StepDescriptor } from "common/components/Flow";
import { UserField, UserInputTypeKey } from "common/components/input/fields";
import { SubformProps } from "common/components/SubformProps";
import { StartIdNowUser, UpdateUser } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { StoreState } from "common/store/types";
import { STORE_CURRENT_USER_ACTION } from "common/store/user/actions";
import { StartIdNowUserManageable, UpdateUserManageable, UserManageable } from "common/store/user/types";
import { isEmptyObject } from "common/utils/utility";
import { errorMessages } from "common/utils/validation";
import { ContentWrapper } from "components/ContentWrapper";
import Navbar from "components/Navbar";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { UserExperienceAdequacyMessage, UserExperienceAdequacyMessageSubform, UserExperienceSubform } from "shared/subforms";

import {
  UserEmploymentSubform,
  UserInfoSubform,
  UserProofSubform,
} from "./subforms/index";

enum Step {
  Info = 0,
  Proof,
  Experience,
  ExperienceAdequacyMessage,
  Count,
}

export const KnowYourCustomerForm = () => {
  // State
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [startIdNowData, setStartIdNowData] = useState<StartIdNowUserManageable>(new StartIdNowUserManageable());

  // Hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => new UserManageable(state.currentUser));

  const {
    execute: startIdNow, loading: startIdNowLoading
  } = useCustomQuery(StartIdNowUser({
    onSuccess: () => {
      history.replace('/idnowstatus');
    },
  }));

  const {
    execute: updateUser, loading: updateUserLoading
  } = useCustomQuery(UpdateUser({
    onSuccess: (data: any) => {
      onSuccessPartialSubmit(data);
    },
    onError: (error: ApiErrors) => {
      if (error === ApiErrors.USER_DUPLICATE_IDENTIFICATION) {
        setError(t("Duplicated passport"));
        setStep(Step.Proof);
      }
    },
  }));

  // Actions
  useEffect(
    () => {
      if (storageUserDetails.isVerificationStarted()) {
        history.goBack();
      }
    },
    []
  );

  // Helpers
  const storeCurrentUser = (values: any) => {
    dispatch({
      type: STORE_CURRENT_USER_ACTION,
      payload: values,
    });
  }

  const validateUser = (
    keys: Array<string>, values: any
  ) => {
    return new UpdateUserManageable(values).validate(
      keys,
      errorMessages
    );
  }

  const triggerNextStep = () => {
    const stepDescriptor = stepDescriptors.get(step) as StepDescriptor;
    setStep(stepDescriptor.nextStep());
  }

  const onSuccessPartialSubmit = (values: any) => {
    if (step === Step.Experience && UserManageable.isExperienceProvided(values)) {
      startIdNow({ variables: { input: startIdNowData.serialize() } });
    } else {
      triggerNextStep();
    }
  }

  const submitUser = (
    values: any, keys: Array<string>
  ) => {
    const input = new UpdateUserManageable(values).serialize(
      keys,
      storageUserDetails
    );

    if (!isEmptyObject(input)) {
      updateUser({ variables: { input } });
    } else {
      onSuccessPartialSubmit(values);
    }
  }

  // Steps
  const infoStepKeys = [
    "cellphone",
    "company",
    "birthDate",
    "birthCity",
    "gender",
    "citizenship",
    "taxNumber",
    "notUSTax",
    "politicallyExposed",
    "politicallyExposedReason",
    "representativeFirstName",
    "representativeLastName",
  ];

  const infoStep : Partial<StepDescriptor> = {
    autofilledData: (values: any) => new UpdateUserManageable(values.automatic()),
    className: "register-user-form-box",
    backButtonVisible: false,
    nextStep: () => Step.Proof,
    nextStepOnNextButton: false,
    onNextButton: (values: any) => {
      submitUser(
        values,
        infoStepKeys
      );
    },
    onValidate: (values: any) => validateUser(
      infoStepKeys,
      values
    ),
    subform: ({
      errors,
      setFieldValue,
      setValues,
      touched,
      values,
    }: SubformProps<UpdateUserManageable>) =>
      <UserInfoSubform
        setFieldValue={setFieldValue}
        setValues={setValues}
        errors={errors}
        touched={touched}
        values={values}
      />,
    title: () => t("Let's get to know you - Part 1"),
    titleClassName: "title--centered",
  }

  const proofStepKeys = [
    "idDocumentExpiryDate",
    "idDocumentIssueDate",
    "idDocumentIssuingAgency",
    "idDocumentType",
    "idDocumentNumber",
    "addressCountry",
    "addressZipcode",
    "addressCity",
    "address",
    "idDocumentCountry",
  ];

  const proofStep : Partial<StepDescriptor> = {
    autofilledData: (values: any) => new UpdateUserManageable(values.automatic()),
    className: "register-user-form-box",
    nextStep: () => Step.Experience,
    nextStepOnNextButton: false,
    onBackButton: (values: any) => {
      storeCurrentUser(values);
    },
    onNextButton: (values: any) => {
      submitUser(
        values,
        proofStepKeys
      );
    },
    onValidate: (values: any) => validateUser(
      proofStepKeys,
      values
    ),
    subform: ({
      errors,
      setFieldValue,
      setValues,
      touched,
      values,
    }: SubformProps<UpdateUserManageable>) =>
      <UserProofSubform
        setFieldValue={setFieldValue}
        setValues={setValues}
        errors={errors}
        touched={touched}
        values={values}
      />,
    title: () => t("Let's get to know you - Part 2"),
    titleClassName: "title--centered",
  }

  const experienceStepKeys = [
    "experience",
    "transactionsPerYear",
    "volumePerYear",
    "employment",
    "highestEducation",
  ];

  const experienceStep : Partial<StepDescriptor> = {
    autofilledData: (values: any) => new UpdateUserManageable(values.automatic()),
    className: "register-user-form-box",
    nextStep: () => Step.ExperienceAdequacyMessage,
    nextStepOnNextButton: false,
    onBackButton: (values: any) => {
      storeCurrentUser(values);
    },
    onNextButton: (values: any) => {
      submitUser(
        values,
        experienceStepKeys
      );
    },
    onValidate: (values: any) => validateUser(
      experienceStepKeys,
      values
    ),
    subform: ({
      errors,
      setFieldValue,
      setValues,
      touched,
      values,
    }: SubformProps<UpdateUserManageable>) =>
      <>
        <UserExperienceSubform
          setFieldValue={setFieldValue}
          setValues={setValues}
          errors={errors}
          touched={touched}
          values={values}
        />

        <UserEmploymentSubform
          setFieldValue={setFieldValue}
          setValues={setValues}
          errors={errors}
          touched={touched}
          values={values}
        />
      </>,
    title: () => t("Knowledge and Experience"),
    titleClassName: "title--centered",
  }

  const experienceAdequacyMessageStep : Partial<StepDescriptor> = {
    autofilledData: (values: any) => new UpdateUserManageable(values.automatic()),
    className: "register-user-form-box",
    dotNumber: 2,
    nextStepOnNextButton: false,
    onNextButton: () => {
      startIdNow({ variables: { input: startIdNowData.serialize() } });
    },
    subform: () =>
      <UserExperienceAdequacyMessageSubform
        view={UserExperienceAdequacyMessage.AdequacyEvaluationNotPossible}
      />,
    title: () => t("Check of adequacy not possible"),
    titleClassName: "title--centered",
  }

  const stepDescriptors = new Map<number, Partial<StepDescriptor>>([
    [Step.Info, infoStep],
    [Step.Proof, proofStep],
    [Step.Experience, experienceStep],
    [Step.ExperienceAdequacyMessage, experienceAdequacyMessageStep],
  ]);

  // Options
  const initialValues = new UpdateUserManageable(storageUserDetails);

  return (
    <>
      <Navbar />

      <ContentWrapper>
        <Box display="grid">
          {process.env.REACT_APP_ENV !== 'prod' &&
          <UserField
            id={"skipIdNow" as UserInputTypeKey}
            className="centered-in-grid"
            values={startIdNowData}
            errors={{}}
            touched={{}}
            setFieldValue={(
              _: string, value: boolean
            ) => setStartIdNowData(new StartIdNowUserManageable({ skipIdNow: value }))}
            setValues={(values: any) => setStartIdNowData(new StartIdNowUserManageable(values))}
          />
          }

          <Flow
            error={error}
            maxNumberOfSteps={Step.Count}
            numberOfSteps={Step.Count - 1}
            initialValues={initialValues}
            step={step}
            setError={setError}
            setStep={setStep}
            stepsDescriptors={stepDescriptors}
            submitInProgress={updateUserLoading || startIdNowLoading}
          />
        </Box>
      </ContentWrapper>
    </>
  );
};
